import React, { useState, useEffect, useRef } from 'react';
import { 
  BarChart3, Users, FileText, DollarSign, TrendingUp, TrendingDown, 
  Calendar, ChevronDown, ChevronRight, X, Filter, ArrowUpRight, ArrowDownRight,
  Share2, Eye, Download, Mail, MessageCircle, Warehouse
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import InventoryStats from '../../components/InventoryStats';

const StatisticsPanel = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [config, setConfig] = useState(null);
  
  // View state
  const [activeView, setActiveView] = useState('general'); // 'general' or 'accumulated'
  const [selectedClient, setSelectedClient] = useState(null);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const statementRef = useRef(null);
  const [showDateFilter, setShowDateFilter] = useState(false);
  
  // Date filter state
  const [dateFilter, setDateFilter] = useState({
    type: 'all', // 'all', 'today', 'yesterday', 'this_week', 'last_week', 'this_month', 'last_month', 'this_year', 'last_year', 'custom'
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [invoicesRes, clientsRes, summaryRes, configRes] = await Promise.all([
        api.get('/invoices', { headers }),
        api.get('/clients', { headers }),
        api.get('/billing/summary', { headers }),
        api.get('/config', { headers }).catch(() => ({ data: {} }))
      ]);

      setInvoices(invoicesRes.data);
      setClients(clientsRes.data);
      setStats(summaryRes.data);
      setConfig(configRes.data);
      
      // Extract all payments from invoices
      const allPayments = [];
      invoicesRes.data.forEach(inv => {
        if (inv.payments && inv.payments.length > 0) {
          inv.payments.forEach(p => {
            allPayments.push({
              ...p,
              invoice_id: inv.id,
              invoice_number: inv.invoice_number,
              client_id: inv.client_id,
              client_name: inv.client_name
            });
          });
        }
      });
      setPayments(allPayments);
      
    } catch (error) {
      toast.error('Error cargando estadísticas');
    } finally {
      setLoading(false);
    }
  };

  // Date filter helper functions
  const getDateRange = (filterType) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (filterType) {
      case 'today':
        return { start: today, end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1) };
      case 'yesterday':
        const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
        return { start: yesterday, end: new Date(today.getTime() - 1) };
      case 'this_week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        return { start: weekStart, end: now };
      case 'last_week':
        const lastWeekEnd = new Date(today);
        lastWeekEnd.setDate(today.getDate() - today.getDay() - 1);
        const lastWeekStart = new Date(lastWeekEnd);
        lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
        return { start: lastWeekStart, end: lastWeekEnd };
      case 'this_month':
        return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: now };
      case 'last_month':
        return { 
          start: new Date(now.getFullYear(), now.getMonth() - 1, 1), 
          end: new Date(now.getFullYear(), now.getMonth(), 0) 
        };
      case 'this_year':
        return { start: new Date(now.getFullYear(), 0, 1), end: now };
      case 'last_year':
        return { 
          start: new Date(now.getFullYear() - 1, 0, 1), 
          end: new Date(now.getFullYear() - 1, 11, 31) 
        };
      case 'custom':
        return { 
          start: dateFilter.startDate ? new Date(dateFilter.startDate) : null, 
          end: dateFilter.endDate ? new Date(dateFilter.endDate + 'T23:59:59') : null 
        };
      default:
        return { start: null, end: null };
    }
  };

  const filterByDate = (items, dateField = 'created_at') => {
    if (dateFilter.type === 'all') return items;
    
    const { start, end } = getDateRange(dateFilter.type);
    if (!start || !end) return items;
    
    return items.filter(item => {
      const itemDate = new Date(item[dateField]);
      return itemDate >= start && itemDate <= end;
    });
  };

  // Get client accumulated data
  const getClientAccumulatedData = (clientId) => {
    const clientInvoices = filterByDate(invoices.filter(inv => inv.client_id === clientId && inv.status !== 'anulada'));
    const clientPayments = filterByDate(payments.filter(p => p.client_id === clientId));
    
    // Build statement entries (extracto)
    const entries = [];
    
    // Add invoices
    clientInvoices.forEach(inv => {
      entries.push({
        type: 'invoice',
        date: inv.created_at,
        description: `Factura #${inv.invoice_number}`,
        invoice_number: inv.invoice_number,
        debit: inv.total,
        credit: 0,
        details: inv.items_detail || inv.items || [],
        notes: inv.notes
      });
    });
    
    // Add payments
    clientPayments.forEach(p => {
      entries.push({
        type: 'payment',
        date: p.created_at,
        description: `Abono - Factura #${p.invoice_number}`,
        invoice_number: p.invoice_number,
        debit: 0,
        credit: p.amount,
        payment_method: p.payment_method,
        notes: p.notes
      });
    });
    
    // Sort by date
    entries.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    // Calculate running balance
    let balance = 0;
    entries.forEach(entry => {
      balance += entry.debit - entry.credit;
      entry.balance = balance;
    });
    
    return {
      entries,
      totalInvoices: clientInvoices.length,
      totalInvoiced: clientInvoices.reduce((sum, inv) => sum + inv.total, 0),
      totalPaid: clientPayments.reduce((sum, p) => sum + p.amount, 0),
      currentBalance: balance
    };
  };

  // Get clients with invoices summary
  const getClientsWithInvoices = () => {
    const clientsMap = {};
    
    invoices.forEach(inv => {
      if (inv.status === 'anulada') return;
      
      if (!clientsMap[inv.client_id]) {
        clientsMap[inv.client_id] = {
          id: inv.client_id,
          name: inv.client_name,
          invoiceCount: 0,
          totalAmount: 0,
          totalPaid: 0,
          balance: 0
        };
      }
      
      clientsMap[inv.client_id].invoiceCount++;
      clientsMap[inv.client_id].totalAmount += inv.total;
      clientsMap[inv.client_id].totalPaid += inv.amount_paid;
      clientsMap[inv.client_id].balance += inv.balance_due;
    });
    
    return Object.values(clientsMap).sort((a, b) => b.balance - a.balance);
  };

  const dateFilterOptions = [
    { id: 'all', label: 'Todo el tiempo' },
    { id: 'today', label: 'Hoy' },
    { id: 'yesterday', label: 'Ayer' },
    { id: 'this_week', label: 'Esta semana' },
    { id: 'last_week', label: 'Semana pasada' },
    { id: 'this_month', label: 'Este mes' },
    { id: 'last_month', label: 'Mes pasado' },
    { id: 'this_year', label: 'Este año' },
    { id: 'last_year', label: 'Año pasado' },
    { id: 'custom', label: 'Personalizado' }
  ];

  const getFilterLabel = () => {
    const option = dateFilterOptions.find(o => o.id === dateFilter.type);
    if (dateFilter.type === 'custom' && dateFilter.startDate && dateFilter.endDate) {
      return `${dateFilter.startDate} - ${dateFilter.endDate}`;
    }
    return option?.label || 'Todo el tiempo';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-white/40">Cargando estadísticas...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* View Switcher */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => { setActiveView('general'); setSelectedClient(null); }}
          className={`px-4 py-2 text-xs uppercase tracking-wider flex items-center gap-2 transition-all ${
            activeView === 'general' 
              ? 'bg-white text-black' 
              : 'text-white/60 hover:text-white border border-white/10'
          }`}
        >
          <BarChart3 className="w-4 h-4" />
          Estadísticas Generales
        </button>
        <button
          onClick={() => setActiveView('accumulated')}
          className={`px-4 py-2 text-xs uppercase tracking-wider flex items-center gap-2 transition-all ${
            activeView === 'accumulated' 
              ? 'bg-white text-black' 
              : 'text-white/60 hover:text-white border border-white/10'
          }`}
        >
          <FileText className="w-4 h-4" />
          Facturas Acumuladas
        </button>
        <button
          onClick={() => { setActiveView('inventory'); setSelectedClient(null); }}
          data-testid="view-inventory-stats"
          className={`px-4 py-2 text-xs uppercase tracking-wider flex items-center gap-2 transition-all ${
            activeView === 'inventory'
              ? 'bg-white text-black'
              : 'text-white/60 hover:text-white border border-white/10'
          }`}
        >
          <Warehouse className="w-4 h-4" />
          Inventario
        </button>
      </div>

      {/* Inventory Stats View */}
      {activeView === 'inventory' && (
        <InventoryStats />
      )}

      {/* General Statistics View */}
      {activeView === 'general' && stats && (
        <div className="space-y-6">
          {/* Main Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <div className="bg-[#0A0A0A] border border-white/10 p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-white/40 flex-shrink-0" />
                <p className="text-[10px] sm:text-xs text-white/40 uppercase tracking-wider truncate">Total Facturas</p>
              </div>
              <p className="text-xl sm:text-3xl font-bold">{stats.total_invoices}</p>
            </div>
            <div className="bg-[#0A0A0A] border border-green-500/20 p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-green-400 flex-shrink-0" />
                <p className="text-[10px] sm:text-xs text-white/40 uppercase tracking-wider truncate">Ventas Totales</p>
              </div>
              <p className="text-base sm:text-2xl lg:text-3xl font-bold text-green-400 break-all">${stats.financial?.total_sales?.toLocaleString() || 0}</p>
            </div>
            <div className="bg-[#0A0A0A] border border-blue-500/20 p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <p className="text-[10px] sm:text-xs text-white/40 uppercase tracking-wider truncate">Total Cobrado</p>
              </div>
              <p className="text-base sm:text-2xl lg:text-3xl font-bold text-blue-400 break-all">${stats.financial?.total_paid?.toLocaleString() || 0}</p>
            </div>
            <div className="bg-[#0A0A0A] border border-red-500/20 p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-2">
                <TrendingDown className="w-4 h-4 text-red-400 flex-shrink-0" />
                <p className="text-[10px] sm:text-xs text-white/40 uppercase tracking-wider truncate">Por Cobrar</p>
              </div>
              <p className="text-base sm:text-2xl lg:text-3xl font-bold text-red-400 break-all">${stats.financial?.total_pending?.toLocaleString() || 0}</p>
            </div>
          </div>

          {/* Status Breakdown */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <div className="bg-[#0A0A0A] border border-yellow-500/20 p-3 sm:p-4">
              <p className="text-[10px] sm:text-xs text-white/40">Pendientes</p>
              <p className="text-lg sm:text-xl font-bold text-yellow-400">{stats.by_status?.pending || 0}</p>
            </div>
            <div className="bg-[#0A0A0A] border border-orange-500/20 p-3 sm:p-4">
              <p className="text-[10px] sm:text-xs text-white/40">Parciales</p>
              <p className="text-lg sm:text-xl font-bold text-orange-400">{stats.by_status?.partial || 0}</p>
            </div>
            <div className="bg-[#0A0A0A] border border-green-500/20 p-3 sm:p-4">
              <p className="text-[10px] sm:text-xs text-white/40">Pagadas</p>
              <p className="text-lg sm:text-xl font-bold text-green-400">{stats.by_status?.paid || 0}</p>
            </div>
            <div className="bg-[#0A0A0A] border border-white/10 p-3 sm:p-4">
              <p className="text-[10px] sm:text-xs text-white/40">Clientes con deuda</p>
              <p className="text-lg sm:text-xl font-bold">{stats.clients_with_debt || 0}</p>
            </div>
          </div>

          {/* Clients Summary */}
          <div className="bg-[#0A0A0A] border border-white/10 p-4 sm:p-6">
            <h3 className="text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Resumen de Clientes
            </h3>
            <div className="grid grid-cols-3 gap-2 sm:gap-4">
              <div className="text-center p-3 sm:p-4 bg-white/5 rounded">
                <p className="text-xl sm:text-2xl font-bold">{stats.total_clients || clients.length}</p>
                <p className="text-[10px] sm:text-xs text-white/40 mt-1">Total</p>
              </div>
              <div className="text-center p-3 sm:p-4 bg-red-500/10 rounded">
                <p className="text-xl sm:text-2xl font-bold text-red-400">{stats.clients_with_debt || 0}</p>
                <p className="text-[10px] sm:text-xs text-white/40 mt-1">Con Deuda</p>
              </div>
              <div className="text-center p-3 sm:p-4 bg-green-500/10 rounded">
                <p className="text-xl sm:text-2xl font-bold text-green-400">{(stats.total_clients || clients.length) - (stats.clients_with_debt || 0)}</p>
                <p className="text-[10px] sm:text-xs text-white/40 mt-1">Sin Deuda</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Accumulated Invoices View */}
      {activeView === 'accumulated' && !selectedClient && (
        <div className="space-y-4">
          {/* Date Filter */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative">
              <button
                onClick={() => setShowDateFilter(!showDateFilter)}
                className="px-4 py-2 bg-[#121212] border border-white/10 text-sm flex items-center gap-2 hover:border-white/30"
              >
                <Calendar className="w-4 h-4" />
                {getFilterLabel()}
                <ChevronDown className="w-4 h-4" />
              </button>
              
              {showDateFilter && (
                <div className="absolute top-full left-0 mt-2 bg-[#121212] border border-white/10 z-50 w-72 p-4">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-xs uppercase tracking-wider text-white/60">Filtros</span>
                    <button 
                      onClick={() => { setDateFilter({ type: 'all', startDate: '', endDate: '' }); }}
                      className="text-xs text-emerald-400 hover:text-emerald-300"
                    >
                      Limpiar
                    </button>
                  </div>
                  
                  {/* Quick filters */}
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {dateFilterOptions.filter(o => o.id !== 'custom' && o.id !== 'all').map(option => (
                      <button
                        key={option.id}
                        onClick={() => { setDateFilter({ ...dateFilter, type: option.id }); setShowDateFilter(false); }}
                        className={`px-3 py-2 text-xs text-left border transition-all ${
                          dateFilter.type === option.id 
                            ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' 
                            : 'border-white/10 hover:border-white/30'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  
                  {/* Custom date range */}
                  <div className="border-t border-white/10 pt-4">
                    <p className="text-xs text-white/40 mb-2 flex items-center gap-2">
                      <Calendar className="w-3 h-3" />
                      Informe el período
                    </p>
                    <div className="space-y-2">
                      <input
                        type="date"
                        value={dateFilter.startDate}
                        onChange={(e) => setDateFilter({ ...dateFilter, type: 'custom', startDate: e.target.value })}
                        className="w-full bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm outline-none"
                        placeholder="Fecha de inicio"
                      />
                      <input
                        type="date"
                        value={dateFilter.endDate}
                        onChange={(e) => setDateFilter({ ...dateFilter, type: 'custom', endDate: e.target.value })}
                        className="w-full bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm outline-none"
                        placeholder="Fecha final"
                      />
                    </div>
                    <button
                      onClick={() => setShowDateFilter(false)}
                      className="w-full mt-3 px-4 py-2 bg-white text-black text-xs uppercase tracking-wider"
                    >
                      Aplicar
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            {dateFilter.type !== 'all' && (
              <button
                onClick={() => setDateFilter({ type: 'all', startDate: '', endDate: '' })}
                className="text-xs text-white/40 hover:text-white flex items-center gap-1"
              >
                <X className="w-3 h-3" />
                Limpiar filtro
              </button>
            )}
          </div>

          {/* Clients List */}
          <div className="bg-[#0A0A0A] border border-white/10">
            <div className="p-4 border-b border-white/10">
              <h3 className="text-sm uppercase tracking-wider flex items-center gap-2">
                <Users className="w-4 h-4" />
                Facturas Acumuladas por Cliente
              </h3>
              <p className="text-xs text-white/40 mt-1">Haga clic en un cliente para ver su extracto detallado</p>
            </div>
            
            <div className="divide-y divide-white/5">
              {getClientsWithInvoices().map(client => (
                <div
                  key={client.id}
                  onClick={() => setSelectedClient(client)}
                  className="p-4 hover:bg-white/5 cursor-pointer flex items-center justify-between transition-all"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                      <span className="text-sm font-bold">{client.name.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="font-medium">{client.name}</p>
                      <p className="text-xs text-white/40">{client.invoiceCount} factura{client.invoiceCount !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-xs text-white/40">Total Facturado</p>
                      <p className="font-semibold">${client.totalAmount.toLocaleString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-white/40">Pagado</p>
                      <p className="font-semibold text-green-400">${client.totalPaid.toLocaleString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-white/40">Saldo</p>
                      <p className={`font-semibold ${client.balance > 0 ? 'text-red-400' : 'text-green-400'}`}>
                        ${client.balance.toLocaleString()}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-white/40" />
                  </div>
                </div>
              ))}
              
              {getClientsWithInvoices().length === 0 && (
                <div className="p-8 text-center text-white/40">
                  No hay clientes con facturas en el período seleccionado
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Client Statement (Extracto) View */}
      {activeView === 'accumulated' && selectedClient && (
        <div className="space-y-4">
          {/* Back button and actions */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSelectedClient(null)}
                className="px-4 py-2 border border-white/10 text-sm flex items-center gap-2 hover:border-white/30"
              >
                <ChevronRight className="w-4 h-4 rotate-180" />
                Volver a Lista
              </button>
              
              {/* Date Filter for statement */}
              <div className="relative">
                <button
                  onClick={() => setShowDateFilter(!showDateFilter)}
                  className="px-4 py-2 bg-[#121212] border border-white/10 text-sm flex items-center gap-2 hover:border-white/30"
                >
                  <Filter className="w-4 h-4" />
                  {getFilterLabel()}
                  <ChevronDown className="w-4 h-4" />
                </button>
                
                {showDateFilter && (
                  <div className="absolute top-full left-0 mt-2 bg-[#121212] border border-white/10 z-50 w-72 p-4">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-xs uppercase tracking-wider text-white/60">Filtros</span>
                      <button 
                        onClick={() => { setDateFilter({ type: 'all', startDate: '', endDate: '' }); }}
                        className="text-xs text-emerald-400 hover:text-emerald-300"
                      >
                        Limpiar
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      {dateFilterOptions.filter(o => o.id !== 'custom' && o.id !== 'all').map(option => (
                        <button
                          key={option.id}
                          onClick={() => { setDateFilter({ ...dateFilter, type: option.id }); setShowDateFilter(false); }}
                          className={`px-3 py-2 text-xs text-left border transition-all ${
                            dateFilter.type === option.id 
                              ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' 
                              : 'border-white/10 hover:border-white/30'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    
                    <div className="border-t border-white/10 pt-4">
                      <p className="text-xs text-white/40 mb-2">Período personalizado</p>
                      <div className="space-y-2">
                        <input
                          type="date"
                          value={dateFilter.startDate}
                          onChange={(e) => setDateFilter({ ...dateFilter, type: 'custom', startDate: e.target.value })}
                          className="w-full bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm outline-none"
                        />
                        <input
                          type="date"
                          value={dateFilter.endDate}
                          onChange={(e) => setDateFilter({ ...dateFilter, type: 'custom', endDate: e.target.value })}
                          className="w-full bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm outline-none"
                        />
                      </div>
                      <button
                        onClick={() => setShowDateFilter(false)}
                        className="w-full mt-3 px-4 py-2 bg-white text-black text-xs uppercase tracking-wider"
                      >
                        Aplicar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Share Button with Dropdown Menu */}
            <div className="relative">
              <button
                onClick={() => setShowShareMenu(!showShareMenu)}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm flex items-center gap-2 transition-all rounded-lg"
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">Compartir</span>
              </button>
              
              {showShareMenu && (
                <>
                  {/* Backdrop */}
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setShowShareMenu(false)}
                  />
                  
                  {/* Dropdown Menu */}
                  <div className="absolute right-0 top-full mt-2 w-56 bg-[#1a1a1a] border border-white/10 rounded-lg shadow-xl z-50 overflow-hidden">
                    <div className="p-2 border-b border-white/10">
                      <p className="text-xs text-white/40 uppercase tracking-wider px-2">Compartir extracto</p>
                    </div>
                    
                    {/* Vista Previa */}
                    <button
                      onClick={() => {
                        setShowShareMenu(false);
                        setShowPreview(true);
                      }}
                      className="w-full px-4 py-3 text-left text-sm flex items-center gap-3 hover:bg-white/5 transition-colors"
                    >
                      <Eye className="w-4 h-4 text-blue-400" />
                      Vista Previa
                    </button>
                    
                    {/* Guardar Imagen */}
                    <button
                      onClick={async () => {
                        setShowShareMenu(false);
                        setGeneratingImage(true);
                        try {
                          const element = statementRef.current;
                          if (element) {
                            const canvas = await html2canvas(element, {
                              backgroundColor: '#0a0a0a',
                              scale: 2,
                              useCORS: true
                            });
                            const link = document.createElement('a');
                            link.download = `extracto_${selectedClient.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.png`;
                            link.href = canvas.toDataURL('image/png');
                            link.click();
                            toast.success('Imagen guardada correctamente');
                          }
                        } catch (error) {
                          toast.error('Error al generar la imagen');
                        } finally {
                          setGeneratingImage(false);
                        }
                      }}
                      disabled={generatingImage}
                      className="w-full px-4 py-3 text-left text-sm flex items-center gap-3 hover:bg-white/5 transition-colors disabled:opacity-50"
                    >
                      <Download className="w-4 h-4 text-purple-400" />
                      {generatingImage ? 'Generando...' : 'Guardar Imagen'}
                    </button>
                    
                    {/* WhatsApp */}
                    <button
                      onClick={async () => {
                        setShowShareMenu(false);
                        setGeneratingImage(true);
                        try {
                          const data = getClientAccumulatedData(selectedClient.id);
                          const message = `*HAUTE - Estado de Cuenta*%0A%0A` +
                            `Cliente: *${selectedClient.name}*%0A` +
                            `Fecha: ${new Date().toLocaleDateString('es-CO')}%0A%0A` +
                            `📊 *Resumen*%0A` +
                            `▪️ Total Facturado: $${data.totalInvoiced.toLocaleString()}%0A` +
                            `▪️ Total Pagado: $${data.totalPaid.toLocaleString()}%0A` +
                            `▪️ *Saldo Actual: $${data.currentBalance.toLocaleString()}*%0A%0A` +
                            `📝 *Movimientos*%0A` +
                            data.entries.slice(-5).map(e => 
                              `${e.type === 'invoice' ? '🔴' : '🟢'} ${new Date(e.date).toLocaleDateString('es-CO')} - ${e.description}: ${e.type === 'invoice' ? '-' : '+'}$${(e.debit || e.credit).toLocaleString()}`
                            ).join('%0A') +
                            (data.entries.length > 5 ? `%0A... y ${data.entries.length - 5} movimientos más` : '') +
                            `%0A%0A_Gracias por su preferencia_`;
                          
                          const whatsappNumber = config?.whatsapp_number || '573000000000';
                          window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
                        } finally {
                          setGeneratingImage(false);
                        }
                      }}
                      className="w-full px-4 py-3 text-left text-sm flex items-center gap-3 hover:bg-white/5 transition-colors"
                    >
                      <MessageCircle className="w-4 h-4 text-green-400" />
                      WhatsApp
                    </button>
                    
                    {/* Email */}
                    <button
                      onClick={() => {
                        setShowShareMenu(false);
                        const data = getClientAccumulatedData(selectedClient.id);
                        const subject = encodeURIComponent(`HAUTE - Estado de Cuenta - ${selectedClient.name}`);
                        const body = encodeURIComponent(
                          `HAUTE - Estado de Cuenta\n\n` +
                          `Cliente: ${selectedClient.name}\n` +
                          `Fecha: ${new Date().toLocaleDateString('es-CO')}\n\n` +
                          `RESUMEN:\n` +
                          `• Total Facturado: $${data.totalInvoiced.toLocaleString()}\n` +
                          `• Total Pagado: $${data.totalPaid.toLocaleString()}\n` +
                          `• Saldo Actual: $${data.currentBalance.toLocaleString()}\n\n` +
                          `MOVIMIENTOS:\n` +
                          data.entries.map(e => 
                            `${new Date(e.date).toLocaleDateString('es-CO')} - ${e.description}: ${e.type === 'invoice' ? 'Débito' : 'Crédito'} $${(e.debit || e.credit).toLocaleString()}`
                          ).join('\n') +
                          `\n\nAtentamente,\nHAUTE`
                        );
                        window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
                      }}
                      className="w-full px-4 py-3 text-left text-sm flex items-center gap-3 hover:bg-white/5 transition-colors"
                    >
                      <Mail className="w-4 h-4 text-orange-400" />
                      Correo Electrónico
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Preview Modal */}
          {showPreview && (
            <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-2 sm:p-4" onClick={() => setShowPreview(false)}>
              <div className="bg-white w-full max-w-4xl max-h-[95vh] overflow-auto rounded-lg" onClick={e => e.stopPropagation()}>
                {/* Preview Content - White theme for printing */}
                <div className="p-4 sm:p-8" style={{ background: 'white', color: '#1a1a1a' }}>
                  {/* Header */}
                  <div className="text-center mb-6 sm:mb-8 pb-4 border-b-2 border-black">
                    <h1 className="text-2xl sm:text-3xl font-bold tracking-[0.3em] text-black">HAUTE</h1>
                    <p className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-widest mt-1">Estado de Cuenta</p>
                  </div>
                  
                  {/* Client Info */}
                  <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-gray-100 border-l-4 border-black">
                    <p className="text-[10px] sm:text-xs text-gray-500 uppercase">Cliente</p>
                    <p className="text-lg sm:text-xl font-semibold text-black">{selectedClient.name}</p>
                    <p className="text-xs sm:text-sm text-gray-500 mt-1">
                      Fecha: {new Date().toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })}
                    </p>
                  </div>
                  
                  {/* Table */}
                  {(() => {
                    const data = getClientAccumulatedData(selectedClient.id);
                    return (
                      <>
                        <div className="overflow-x-auto mb-4 sm:mb-6">
                          <table className="w-full min-w-[400px] border-collapse">
                            <thead>
                              <tr className="bg-black text-white">
                                <th className="p-2 sm:p-3 text-left text-[10px] sm:text-xs uppercase">Fecha</th>
                                <th className="p-2 sm:p-3 text-left text-[10px] sm:text-xs uppercase">Descripción</th>
                                <th className="p-2 sm:p-3 text-right text-[10px] sm:text-xs uppercase">Débito</th>
                                <th className="p-2 sm:p-3 text-right text-[10px] sm:text-xs uppercase">Crédito</th>
                              </tr>
                            </thead>
                            <tbody>
                              {data.entries.map((entry, idx) => (
                                <tr key={idx} className="border-b border-gray-200">
                                  <td className="p-2 sm:p-3 text-xs sm:text-sm whitespace-nowrap">{new Date(entry.date).toLocaleDateString('es-CO', { day: '2-digit', month: 'short' })}</td>
                                  <td className="p-2 sm:p-3 text-xs sm:text-sm">
                                    <span className={`inline-block w-2 h-2 rounded-full mr-1 sm:mr-2 ${entry.type === 'invoice' ? 'bg-red-500' : 'bg-green-500'}`}></span>
                                    <span className="truncate">{entry.description}</span>
                                  </td>
                                  <td className="p-2 sm:p-3 text-right text-xs sm:text-sm text-red-600 font-mono whitespace-nowrap">{entry.debit > 0 ? `$${entry.debit.toLocaleString()}` : ''}</td>
                                  <td className="p-2 sm:p-3 text-right text-xs sm:text-sm text-green-600 font-mono whitespace-nowrap">{entry.credit > 0 ? `$${entry.credit.toLocaleString()}` : ''}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Summary - Vertical stack on mobile */}
                        <div className="bg-black text-white p-4 sm:p-6 rounded">
                          <div className="flex flex-col sm:flex-row sm:justify-between gap-4 sm:gap-6">
                            <div className="flex justify-between sm:flex-col sm:text-center">
                              <p className="text-[10px] sm:text-xs text-gray-400 uppercase">Total Facturado</p>
                              <p className="text-base sm:text-xl font-bold font-mono">${data.totalInvoiced.toLocaleString()}</p>
                            </div>
                            <div className="flex justify-between sm:flex-col sm:text-center">
                              <p className="text-[10px] sm:text-xs text-gray-400 uppercase">Total Pagado</p>
                              <p className="text-base sm:text-xl font-bold font-mono text-green-400">${data.totalPaid.toLocaleString()}</p>
                            </div>
                            <div className="flex justify-between sm:flex-col sm:text-center border-t sm:border-t-0 border-white/20 pt-3 sm:pt-0">
                              <p className="text-[10px] sm:text-xs text-gray-400 uppercase">Saldo Pendiente</p>
                              <p className={`text-lg sm:text-xl font-bold font-mono ${data.currentBalance > 0 ? 'text-red-400' : 'text-green-400'}`}>${data.currentBalance.toLocaleString()}</p>
                            </div>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
                
                {/* Preview Actions */}
                <div className="sticky bottom-0 p-3 sm:p-4 bg-gray-100 border-t flex justify-end gap-3">
                  <button
                    onClick={() => setShowPreview(false)}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-black"
                  >
                    Cerrar
                  </button>
                  <button
                    onClick={() => {
                      window.print();
                    }}
                    className="px-4 py-2 bg-black text-white text-sm rounded hover:bg-gray-800"
                  >
                    Imprimir
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Printable Statement Area */}
          <div id="statement-print-area" ref={statementRef} className="bg-[#0A0A0A] border border-white/10 overflow-hidden">
            {/* Elegant Header */}
            <div className="bg-gradient-to-r from-white/5 to-transparent p-4 sm:p-8 border-b border-white/10">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="text-center sm:text-left">
                  <h1 className="text-2xl sm:text-3xl font-bold tracking-[0.3em] mb-1">HAUTE</h1>
                  <p className="text-[10px] sm:text-xs text-white/40 uppercase tracking-[0.2em]">Estado de Cuenta</p>
                </div>
                <div className="text-center sm:text-right">
                  <p className="text-[10px] sm:text-xs text-white/40 uppercase tracking-wider mb-1">Cliente</p>
                  <p className="text-xl sm:text-2xl font-semibold">{selectedClient.name}</p>
                  <p className="text-[10px] sm:text-xs text-white/40 mt-2">
                    Emitido: {new Date().toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Statement Table */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[500px]">
                <thead>
                  <tr className="bg-white/5">
                    <th className="text-left text-[9px] sm:text-[10px] uppercase tracking-wider sm:tracking-[0.15em] text-white/50 p-2 sm:p-4 font-medium">Fecha</th>
                    <th className="text-left text-[9px] sm:text-[10px] uppercase tracking-wider sm:tracking-[0.15em] text-white/50 p-2 sm:p-4 font-medium">Descripción</th>
                    <th className="text-right text-[9px] sm:text-[10px] uppercase tracking-wider sm:tracking-[0.15em] text-white/50 p-2 sm:p-4 font-medium">Débito</th>
                    <th className="text-right text-[9px] sm:text-[10px] uppercase tracking-wider sm:tracking-[0.15em] text-white/50 p-2 sm:p-4 font-medium">Crédito</th>
                    <th className="text-right text-[9px] sm:text-[10px] uppercase tracking-wider sm:tracking-[0.15em] text-white/50 p-2 sm:p-4 font-medium">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const data = getClientAccumulatedData(selectedClient.id);
                    return data.entries.map((entry, idx) => (
                      <tr 
                        key={idx} 
                        className={`border-b border-white/5 hover:bg-white/5 transition-colors ${
                          entry.type === 'payment' ? 'bg-green-500/5' : ''
                        }`}
                      >
                        <td className="p-2 sm:p-4">
                          <div className="text-xs sm:text-sm font-medium">
                            {new Date(entry.date).toLocaleDateString('es-CO', { day: '2-digit', month: 'short' })}
                          </div>
                          <div className="text-[9px] sm:text-[10px] text-white/30">
                            {new Date(entry.date).toLocaleDateString('es-CO', { year: 'numeric' })}
                          </div>
                        </td>
                        <td className="p-2 sm:p-4">
                          <div className="flex items-start gap-2 sm:gap-3">
                            <div className={`w-2 h-2 rounded-full mt-1 sm:mt-1.5 flex-shrink-0 ${
                              entry.type === 'invoice' ? 'bg-red-400' : 'bg-green-400'
                            }`} />
                            <div className="min-w-0">
                              <p className="text-xs sm:text-sm font-medium truncate">{entry.description}</p>
                              {entry.type === 'payment' && entry.payment_method && (
                                <p className="text-[10px] sm:text-[11px] text-white/40 mt-0.5">
                                  Método: <span className="text-white/60">{entry.payment_method}</span>
                                </p>
                              )}
                              {entry.notes && entry.type === 'invoice' && (
                                <p className="text-[10px] sm:text-[11px] text-white/40 mt-0.5 truncate">{entry.notes}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="p-2 sm:p-4 text-right">
                          {entry.debit > 0 && (
                            <span className="text-red-400 font-semibold font-mono text-xs sm:text-sm">
                              ${entry.debit.toLocaleString()}
                            </span>
                          )}
                        </td>
                        <td className="p-2 sm:p-4 text-right">
                          {entry.credit > 0 && (
                            <span className="text-green-400 font-semibold font-mono text-xs sm:text-sm">
                              ${entry.credit.toLocaleString()}
                            </span>
                          )}
                        </td>
                        <td className="p-2 sm:p-4 text-right">
                          <span className={`font-bold font-mono text-xs sm:text-sm ${
                            entry.balance > 0 ? 'text-red-400' : 'text-green-400'
                          }`}>
                            ${entry.balance.toLocaleString()}
                          </span>
                        </td>
                      </tr>
                    ));
                  })()}
                  
                  {getClientAccumulatedData(selectedClient.id).entries.length === 0 && (
                    <tr>
                      <td colSpan="5" className="p-8 sm:p-12 text-center text-white/40">
                        <div className="flex flex-col items-center gap-2">
                          <FileText className="w-8 h-8 opacity-30" />
                          <p className="text-sm">No hay movimientos en el período seleccionado</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Summary Footer - Totals at bottom */}
            {(() => {
              const data = getClientAccumulatedData(selectedClient.id);
              return (
                <div className="bg-gradient-to-r from-white/10 to-white/5 p-4 sm:p-8 border-t border-white/10">
                  <div className="grid grid-cols-3 gap-2 sm:gap-8">
                    <div className="text-center p-3 sm:p-6 bg-black/20 rounded-lg">
                      <p className="text-[9px] sm:text-[10px] uppercase tracking-wider sm:tracking-[0.2em] text-white/40 mb-1 sm:mb-3">Total Facturado</p>
                      <p className="text-sm sm:text-xl lg:text-3xl font-bold font-mono break-all">${data.totalInvoiced.toLocaleString()}</p>
                    </div>
                    <div className="text-center p-3 sm:p-6 bg-green-500/10 rounded-lg border border-green-500/20">
                      <p className="text-[9px] sm:text-[10px] uppercase tracking-wider sm:tracking-[0.2em] text-green-400/60 mb-1 sm:mb-3">Total Pagado</p>
                      <p className="text-sm sm:text-xl lg:text-3xl font-bold font-mono text-green-400 break-all">${data.totalPaid.toLocaleString()}</p>
                    </div>
                    <div className={`text-center p-3 sm:p-6 rounded-lg border ${
                      data.currentBalance > 0 
                        ? 'bg-red-500/10 border-red-500/20' 
                        : 'bg-green-500/10 border-green-500/20'
                    }`}>
                      <p className={`text-[9px] sm:text-[10px] uppercase tracking-wider sm:tracking-[0.2em] mb-1 sm:mb-3 ${
                        data.currentBalance > 0 ? 'text-red-400/60' : 'text-green-400/60'
                      }`}>Saldo Actual</p>
                      <p className={`text-sm sm:text-xl lg:text-3xl font-bold font-mono break-all ${
                        data.currentBalance > 0 ? 'text-red-400' : 'text-green-400'
                      }`}>
                        ${data.currentBalance.toLocaleString()}
                      </p>
                      {data.currentBalance > 0 && (
                        <p className="text-[8px] sm:text-[10px] text-red-400/60 mt-1 sm:mt-2 uppercase tracking-wider">Pendiente</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Quick Stats */}
                  <div className="flex justify-center gap-4 sm:gap-8 mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-white/10">
                    <div className="text-center">
                      <p className="text-lg sm:text-2xl font-bold">{data.totalInvoices}</p>
                      <p className="text-[9px] sm:text-[10px] text-white/40 uppercase tracking-wider">Facturas</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg sm:text-2xl font-bold">{data.entries.filter(e => e.type === 'payment').length}</p>
                      <p className="text-[9px] sm:text-[10px] text-white/40 uppercase tracking-wider">Pagos</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg sm:text-2xl font-bold">
                        {data.totalInvoiced > 0 ? Math.round((data.totalPaid / data.totalInvoiced) * 100) : 0}%
                      </p>
                      <p className="text-[9px] sm:text-[10px] text-white/40 uppercase tracking-wider">Pagado</p>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default StatisticsPanel;
